import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { config } from "../../utils/config";
import { getCookie } from "../../utils/cookieHandler";
import axios from "axios";
import Select from "react-select";
import * as XLSX from "xlsx";

const EMPTY_ROW = {
  reservationId: null,
  type: null,
  itemId: null,
  value: "",
  returnDateTime: "",
  transactionMethodId: null,
  description: "",
};

const TYPE_OPTIONS = [
  { value: "Charge", label: "Charge" },
  { value: "Time Adjustment", label: "Time Adjustment" },
  { value: "Transaction", label: "Transaction" },
];

const SmartBillPage = () => {
  const navigate = useNavigate();

  // Role guard — admin only
  useEffect(() => {
    const currentRole = getCookie("_r");
    const adminRole = process.env.REACT_APP_ADMIN_ROLE;
    if (currentRole !== adminRole) {
      navigate("/", { replace: true });
    }
  }, [navigate]);

  // Data state
  const [reservations, setReservations] = useState([]);
  const [chargeValues, setChargeValues] = useState([]);
  const [transactionTypes, setTransactionTypes] = useState([]);
  const [transactionMethods, setTransactionMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveResults, setSaveResults] = useState(null);
  const [importWarnings, setImportWarnings] = useState([]);

  // Rows state
  const [rows, setRows] = useState([{ ...EMPTY_ROW, _key: Date.now() }]);

  // Drag-and-drop state
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const getAuthHeaders = useCallback(() => {
    return {
      headers: { Authorization: `Bearer ${getCookie("_AT")}` },
    };
  }, []);

  // Fetch all required data on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const headers = getAuthHeaders();

        const [resReservations, resCharges, resTypes, resMethods] =
          await Promise.all([
            axios.get(
              `${config.apiUrl}/api/smart-bill/pickedup-reservations`,
              headers
            ),
            axios.get(`${config.apiUrl}/api/chargesvalues`, headers),
            axios.get(`${config.apiUrl}/api/transactiontypes`, headers),
            axios.get(`${config.apiUrl}/api/transactionmethods`, headers),
          ]);

        setReservations(resReservations.data?.data || []);
        setChargeValues(resCharges.data || []);
        setTransactionTypes(resTypes.data || []);
        setTransactionMethods(resMethods.data || []);
      } catch (error) {
        console.error("Error fetching Smart-Bill data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [getAuthHeaders]);

  // Build dropdown options
  const reservationOptions = reservations.map((r) => ({
    value: r.id,
    label: r.plate,
  }));

  const chargeOptions = chargeValues.map((c) => ({
    value: c.id,
    label: c.name,
  }));

  const transactionTypeOptions = transactionTypes.map((t) => ({
    value: t.id,
    label: t.name,
  }));

  const transactionMethodOptions = transactionMethods.map((m) => ({
    value: m.id,
    label: m.name,
  }));

  const getItemOptions = (type) => {
    switch (type) {
      case "Charge":
        return chargeOptions;
      case "Transaction":
        return transactionTypeOptions;
      case "Time Adjustment":
        return [{ value: "time_adj", label: "Return Date Adjustment" }];
      default:
        return [];
    }
  };

  // Row management
  const updateRow = (index, field, value) => {
    setRows((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      // Reset dependent fields when type changes
      if (field === "type") {
        updated[index].itemId = null;
        updated[index].returnDateTime = "";
        updated[index].transactionMethodId = null;
      }
      return updated;
    });
  };

  const addRow = () => {
    setRows((prev) => [
      ...prev,
      { ...EMPTY_ROW, _key: Date.now() + Math.random() },
    ]);
  };

  const removeRow = (index) => {
    setRows((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((_, i) => i !== index);
    });
  };

  // Save batch
  const handleSave = async () => {
    setImportWarnings([]);

    // Validate rows
    const validRows = rows.filter(
      (r) =>
        r.reservationId &&
        r.type &&
        (r.type === "Time Adjustment" ? true : r.itemId) &&
        r.value
    );

    if (validRows.length === 0) {
      setSaveResults({
        success: false,
        message:
          "No valid rows to save. Each row needs at least a reservation, type, and value.",
      });
      return;
    }

    // Pre-flight validation with human-readable messages
    const validationErrors = [];
    for (let i = 0; i < validRows.length; i++) {
      const r = validRows[i];
      if (r.type === "Time Adjustment" && !r.returnDateTime) {
        validationErrors.push(
          `Row ${i + 1}: Time Adjustment requires a Return DateTime`
        );
      }
      if (r.type === "Transaction" && !r.transactionMethodId) {
        validationErrors.push(
          `Row ${i + 1}: Transaction requires a Transaction Method`
        );
      }
      if (isNaN(parseFloat(r.value)) || parseFloat(r.value) === 0) {
        validationErrors.push(
          `Row ${i + 1}: Value must be a non-zero number`
        );
      }
    }

    if (validationErrors.length > 0) {
      setSaveResults({
        success: false,
        message: "Validation failed. Please fix the following:",
        results: validationErrors.map((msg, i) => ({
          rowIndex: i,
          success: false,
          message: msg,
        })),
      });
      return;
    }

    setSaving(true);
    setSaveResults(null);

    try {
      const payload = validRows.map((r) => ({
        reservationId: r.reservationId,
        type: r.type,
        itemId: r.type === "Time Adjustment" ? null : r.itemId,
        value: parseFloat(r.value),
        returnDateTime: r.returnDateTime || null,
        transactionMethodId: r.transactionMethodId || null,
        description: r.description || "",
      }));

      const response = await axios.post(
        `${config.apiUrl}/api/smart-bill/process-batch`,
        { rows: payload },
        getAuthHeaders()
      );

      setSaveResults(response.data);

      // Clear successful rows
      if (response.data?.results) {
        const failedIndices = response.data.results
          .filter((r) => !r.success)
          .map((r) => r.rowIndex);

        if (failedIndices.length === 0) {
          setRows([{ ...EMPTY_ROW, _key: Date.now() }]);
        } else {
          setRows(
            validRows
              .filter((_, i) => failedIndices.includes(i))
              .map((r) => ({ ...r, _key: Date.now() + Math.random() }))
          );
        }
      }
    } catch (error) {
      setSaveResults({
        success: false,
        message:
          error.response?.data?.message ||
          error.message ||
          "Failed to save batch",
      });
    } finally {
      setSaving(false);
    }
  };

  // Download template
  const handleDownloadTemplate = () => {
    const wb = XLSX.utils.book_new();

    // --- Main Sheet ---
    const headers = [
      "Vehicle Plate",
      "Type",
      "Item",
      "Value",
      "Return DateTime",
      "Transaction Method",
      "Description",
    ];

    const DATA_ROWS = 50;

    // Create main sheet with headers + empty data rows
    const mainData = [headers];
    for (let i = 0; i < DATA_ROWS; i++) {
      mainData.push(["", "", "", "", "", "", ""]);
    }
    const wsMain = XLSX.utils.aoa_to_sheet(mainData);

    // Column widths
    wsMain["!cols"] = [
      { wch: 18 },
      { wch: 18 },
      { wch: 30 },
      { wch: 12 },
      { wch: 22 },
      { wch: 20 },
      { wch: 35 }, // Description
    ];

    XLSX.utils.book_append_sheet(wb, wsMain, "Smart-Bill");

    // --- Reference Sheet ---
    const maxLen = Math.max(
      chargeValues.length,
      transactionTypes.length,
      transactionMethods.length,
      reservations.length,
      1 // For time adjustment
    );
    const refData = [
      [
        "Charge Types",
        "Transaction Types",
        "Transaction Methods",
        "Available Plates",
        "Time Adj Items",
      ],
    ];
    for (let i = 0; i < maxLen; i++) {
      refData.push([
        chargeValues[i]?.name || "",
        transactionTypes[i]?.name || "",
        transactionMethods[i]?.name || "",
        reservations[i]?.plate || "",
        i === 0 ? "Return Date Adjustment" : "", // The only valid item for time adj
      ]);
    }
    const wsRef = XLSX.utils.aoa_to_sheet(refData);
    wsRef["!cols"] = [
      { wch: 30 },
      { wch: 25 },
      { wch: 22 },
      { wch: 20 },
      { wch: 25 },
    ];

    XLSX.utils.book_append_sheet(wb, wsRef, "Reference Data");

    XLSX.writeFile(wb, "smart_bill_template.xlsx");
  };

  // Import sheet
  const processImportedFile = (file) => {
    if (!file) return;

    const fileName = file.name || "";
    const fileExt = fileName
      .substring(fileName.lastIndexOf("."))
      .toLowerCase();
    const validExtensions = [".xlsx", ".xls", ".csv"];

    if (!validExtensions.includes(fileExt)) {
      setSaveResults({
        success: false,
        message: `Invalid file type "${fileExt}". Please upload .xlsx, .xls, or .csv files only.`,
      });
      return;
    }

    const reader = new FileReader();

    reader.onerror = () => {
      setSaveResults({
        success: false,
        message: `Could not read file "${fileName}". The file may be corrupted or too large.`,
      });
    };

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        let wb;
        try {
          wb = XLSX.read(data, { type: "array" });
        } catch (parseErr) {
          setSaveResults({
            success: false,
            message: `Could not parse "${fileName}". Make sure it is a valid Excel or CSV file. Error: ${parseErr.message}`,
          });
          return;
        }

        if (!wb.SheetNames || wb.SheetNames.length === 0) {
          setSaveResults({
            success: false,
            message: `The file "${fileName}" contains no sheets.`,
          });
          return;
        }

        // Read the first sheet (Smart-Bill or first available)
        const sheetName =
          wb.SheetNames.find((n) => n === "Smart-Bill") || wb.SheetNames[0];
        const ws = wb.Sheets[sheetName];

        if (!ws) {
          setSaveResults({
            success: false,
            message: `Sheet "${sheetName}" is empty or could not be read.`,
          });
          return;
        }

        const jsonData = XLSX.utils.sheet_to_json(ws, { header: 1 });

        if (!jsonData || jsonData.length <= 1) {
          setSaveResults({
            success: false,
            message:
              "No data rows found. The file should have a header row and at least one data row.",
          });
          return;
        }

        // Skip header row, process data rows
        const importedRows = [];
        const warnings = [];

        for (let i = 1; i < jsonData.length; i++) {
          const row = jsonData[i];
          const rowNum = i + 1; // Excel-style 1-indexed, accounting for header

          // Skip fully empty rows
          if (!row || row.every((cell) => !cell && cell !== 0)) continue;

          const plate = String(row[0] || "").trim();
          const typeName = String(row[1] || "").trim();
          const itemName = String(row[2] || "").trim();
          const value = row[3];
          const returnDT = row[4] ? String(row[4]).trim() : "";
          const methodName = String(row[5] || "").trim();
          const description = String(row[6] || "").trim();

          // --- Resolve reservation by plate ---
          let matchedResId = null;
          if (!plate) {
            warnings.push(`Row ${rowNum}: Vehicle Plate is empty`);
          } else {
            const matchedRes = reservations.find(
              (r) =>
                r.plate?.toLowerCase().trim() === plate.toLowerCase()
            );
            if (matchedRes) {
              matchedResId = matchedRes.id;
            } else {
              warnings.push(
                `Row ${rowNum}: Plate "${plate}" not found among picked-up reservations`
              );
            }
          }

          // --- Resolve type ---
          let matchedTypeValue = null;
          if (!typeName) {
            warnings.push(`Row ${rowNum}: Type is empty`);
          } else {
            const matchedType = TYPE_OPTIONS.find(
              (t) => t.label.toLowerCase() === typeName.toLowerCase()
            );
            if (matchedType) {
              matchedTypeValue = matchedType.value;
            } else {
              warnings.push(
                `Row ${rowNum}: Type "${typeName}" is not valid. Use: Charge, Time Adjustment, or Transaction`
              );
            }
          }

          // --- Resolve item ---
          let matchedItemId = null;
          if (matchedTypeValue === "Charge") {
            if (!itemName) {
              warnings.push(`Row ${rowNum}: Item (Charge type) is empty`);
            } else {
              const found = chargeValues.find(
                (c) => c.name.toLowerCase() === itemName.toLowerCase()
              );
              if (found) {
                matchedItemId = found.id;
              } else {
                warnings.push(
                  `Row ${rowNum}: Charge type "${itemName}" not found. Check the Reference Data sheet for valid names.`
                );
              }
            }
          } else if (matchedTypeValue === "Transaction") {
            if (!itemName) {
              warnings.push(
                `Row ${rowNum}: Item (Transaction type) is empty`
              );
            } else {
              const found = transactionTypes.find(
                (t) => t.name.toLowerCase() === itemName.toLowerCase()
              );
              if (found) {
                matchedItemId = found.id;
              } else {
                warnings.push(
                  `Row ${rowNum}: Transaction type "${itemName}" not found. Check the Reference Data sheet for valid names.`
                );
              }
            }
          } else if (matchedTypeValue === "Time Adjustment") {
            matchedItemId = "time_adj";
          }

          // --- Validate value ---
          let parsedValue = "";
          if (value == null || value === "") {
            warnings.push(`Row ${rowNum}: Value is empty`);
          } else if (isNaN(parseFloat(value))) {
            warnings.push(
              `Row ${rowNum}: Value "${value}" is not a valid number`
            );
          } else {
            parsedValue = String(value);
          }

          // --- Validate return datetime for time adjustment ---
          if (matchedTypeValue === "Time Adjustment" && !returnDT) {
            warnings.push(
              `Row ${rowNum}: Return DateTime is required for Time Adjustment`
            );
          }

          // --- Resolve transaction method ---
          let matchedMethodId = null;
          if (matchedTypeValue === "Transaction") {
            if (!methodName) {
              warnings.push(
                `Row ${rowNum}: Transaction Method is required for Transaction type`
              );
            } else {
              const found = transactionMethods.find(
                (m) => m.name.toLowerCase() === methodName.toLowerCase()
              );
              if (found) {
                matchedMethodId = found.id;
              } else {
                warnings.push(
                  `Row ${rowNum}: Transaction method "${methodName}" not found. Check the Reference Data sheet for valid names.`
                );
              }
            }
          }

          importedRows.push({
            _key: Date.now() + i + Math.random(),
            reservationId: matchedResId,
            type: matchedTypeValue,
            itemId: matchedItemId,
            value: parsedValue,
            returnDateTime: returnDT,
            transactionMethodId: matchedMethodId,
            description: description,
          });
        }

        if (importedRows.length === 0) {
          setSaveResults({
            success: false,
            message:
              "All rows in the file were empty. Please add data below the header row.",
          });
          return;
        }

        // Set import warnings
        setImportWarnings(warnings);

        // Add imported rows (replace empty initial row if present)
        setRows((prev) => {
          const hasOnlyEmpty =
            prev.length === 1 && !prev[0].reservationId && !prev[0].type;
          return hasOnlyEmpty ? importedRows : [...prev, ...importedRows];
        });

        const warningText =
          warnings.length > 0
            ? ` with ${warnings.length} warning(s) — review highlighted fields below.`
            : ". Review and edit before saving.";

        setSaveResults({
          success: warnings.length === 0,
          message: `Imported ${importedRows.length} row(s)${warningText}`,
        });
      } catch (error) {
        setSaveResults({
          success: false,
          message: `Unexpected error reading "${fileName}": ${error.message}`,
        });
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleFileSelect = (e) => {
    processImportedFile(e.target.files?.[0]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    processImportedFile(e.dataTransfer.files?.[0]);
  };

  // Custom styles for react-select — clean white theme matching the admin UI
  const selectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: "36px",
      fontSize: "13px",
      borderColor: state.isFocused ? "#6366f1" : "#e5e7eb",
      backgroundColor: "#fff",
      borderRadius: "6px",
      boxShadow: state.isFocused ? "0 0 0 1px #6366f1" : "none",
      "&:hover": { borderColor: "#a5b4fc" },
    }),
    singleValue: (base) => ({ ...base, color: "#111827" }),
    input: (base) => ({ ...base, color: "#111827" }),
    menu: (base) => ({
      ...base,
      backgroundColor: "#fff",
      border: "1px solid #e5e7eb",
      borderRadius: "8px",
      boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -2px rgba(0,0,0,0.1)",
      zIndex: 50,
      overflow: "hidden",
    }),
    menuList: (base) => ({
      ...base,
      padding: "4px",
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? "#4f46e5"
        : state.isFocused
        ? "#eef2ff"
        : "#fff",
      color: state.isSelected ? "#fff" : "#374151",
      fontSize: "13px",
      borderRadius: "4px",
      "&:active": { backgroundColor: "#c7d2fe" },
    }),
    placeholder: (base) => ({
      ...base,
      color: "#9ca3af",
      fontSize: "13px",
    }),
    indicatorSeparator: () => ({ display: "none" }),
    dropdownIndicator: (base, state) => ({
      ...base,
      padding: "4px",
      color: state.isFocused ? "#6366f1" : "#9ca3af",
      "&:hover": { color: "#6366f1" },
    }),
    clearIndicator: (base) => ({
      ...base,
      padding: "4px",
      color: "#9ca3af",
      "&:hover": { color: "#ef4444" },
    }),
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-3">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-indigo-500"></div>
          <p className="text-gray-400 text-sm">Loading Smart-Bill data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Action Bar */}
      <div className="flex flex-wrap items-center gap-3">
        <button
          onClick={handleDownloadTemplate}
          className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 transition-colors"
        >
          <svg
            className="h-4 w-4 text-gray-500"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth="1.5"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3"
            />
          </svg>
          Download Template
        </button>

        <button
          onClick={() => fileInputRef.current?.click()}
          className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 transition-colors"
        >
          <svg
            className="h-4 w-4 text-gray-500"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth="1.5"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5"
            />
          </svg>
          Import Sheet
        </button>

        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls,.csv"
          onChange={handleFileSelect}
          className="hidden"
        />

        <div className="flex-1" />

        <button
          onClick={addRow}
          className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-500 transition-colors"
        >
          <svg
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth="1.5"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 4.5v15m7.5-7.5h-15"
            />
          </svg>
          Add Row
        </button>

        <button
          onClick={handleSave}
          disabled={saving}
          className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {saving ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
              Saving...
            </>
          ) : (
            <>
              <svg
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M4.5 12.75l6 6 9-13.5"
                />
              </svg>
              Save Batch
            </>
          )}
        </button>
      </div>

      {/* Save Results / Messages */}
      {saveResults && (
        <div
          className={`rounded-lg p-4 text-sm ${
            saveResults.success
              ? "bg-emerald-50 border border-emerald-200 text-emerald-800"
              : "bg-red-50 border border-red-200 text-red-800"
          }`}
        >
          <p className="font-medium">{saveResults.message}</p>
          {saveResults.results && (
            <div className="mt-2 space-y-1">
              {saveResults.results
                .filter((r) => !r.success)
                .map((r, i) => (
                  <p key={i} className="text-red-600 text-xs">
                    • {r.message}
                  </p>
                ))}
            </div>
          )}
        </div>
      )}

      {/* Import Warnings */}
      {importWarnings.length > 0 && (
        <div className="rounded-lg p-4 text-sm bg-amber-50 border border-amber-200 text-amber-800">
          <div className="flex items-start gap-2">
            <svg
              className="h-5 w-5 text-amber-500 mt-0.5 flex-shrink-0"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="1.5"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
              />
            </svg>
            <div>
              <p className="font-medium mb-1">
                Import completed with {importWarnings.length} warning
                {importWarnings.length !== 1 ? "s" : ""}:
              </p>
              <div className="space-y-0.5 max-h-40 overflow-y-auto">
                {importWarnings.map((w, i) => (
                  <p key={i} className="text-xs text-amber-700">
                    • {w}
                  </p>
                ))}
              </div>
            </div>
          </div>
          <button
            onClick={() => setImportWarnings([])}
            className="mt-2 text-xs text-amber-600 hover:text-amber-800 underline"
          >
            Dismiss warnings
          </button>
        </div>
      )}

      {/* Table Container */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className="relative"
      >
        {/* Drag overlay */}
        {isDragOver && (
          <div className="absolute inset-0 z-10 rounded-xl border-2 border-dashed border-indigo-400 bg-indigo-50/90 flex items-center justify-center">
            <div className="text-center">
              <svg
                className="h-10 w-10 text-indigo-400 mx-auto mb-2"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5"
                />
              </svg>
              <p className="text-indigo-600 font-medium text-sm">
                Drop your .xlsx or .csv file here
              </p>
            </div>
          </div>
        )}

        {/* Spreadsheet Table */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-10">
                    #
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider min-w-[200px]">
                    Reservation
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider min-w-[170px]">
                    Type
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider min-w-[220px]">
                    Item
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider min-w-[120px]">
                    Value
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider min-w-[200px]">
                    Return DateTime
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider min-w-[180px]">
                    Transaction Method
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider min-w-[200px]">
                    Description
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider w-12">
                    &nbsp;
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {rows.map((row, index) => (
                  <tr
                    key={row._key}
                    className="hover:bg-gray-50/50 transition-colors"
                  >
                    {/* Row number */}
                    <td className="px-3 py-2.5 text-xs text-gray-400 font-mono">
                      {index + 1}
                    </td>

                    {/* Reservation */}
                    <td className="px-3 py-2">
                      <Select
                        options={reservationOptions}
                        value={
                          reservationOptions.find(
                            (o) => o.value === row.reservationId
                          ) || null
                        }
                        onChange={(opt) =>
                          updateRow(
                            index,
                            "reservationId",
                            opt?.value || null
                          )
                        }
                        placeholder="Select plate..."
                        isClearable
                        styles={selectStyles}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                      />
                    </td>

                    {/* Type */}
                    <td className="px-3 py-2">
                      <Select
                        options={TYPE_OPTIONS}
                        value={
                          TYPE_OPTIONS.find(
                            (o) => o.value === row.type
                          ) || null
                        }
                        onChange={(opt) =>
                          updateRow(index, "type", opt?.value || null)
                        }
                        placeholder="Select type..."
                        isClearable
                        styles={selectStyles}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                      />
                    </td>

                    {/* Item */}
                    <td className="px-3 py-2">
                      {row.type ? (
                        <Select
                          options={getItemOptions(row.type)}
                          value={
                            getItemOptions(row.type).find(
                              (o) => o.value === row.itemId
                            ) || null
                          }
                          onChange={(opt) =>
                            updateRow(
                              index,
                              "itemId",
                              opt?.value || null
                            )
                          }
                          placeholder="Select item..."
                          isClearable
                          styles={selectStyles}
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                        />
                      ) : (
                        <span className="text-xs text-gray-400 italic pl-2">
                          Select a type first
                        </span>
                      )}
                    </td>

                    {/* Value */}
                    <td className="px-3 py-2">
                      <input
                        type="number"
                        step="0.01"
                        value={row.value}
                        onChange={(e) =>
                          updateRow(index, "value", e.target.value)
                        }
                        placeholder="0.00"
                        className="w-full rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-900 placeholder-gray-400 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 focus:outline-none transition-colors"
                      />
                    </td>

                    {/* Return DateTime — only for Time Adjustment */}
                    <td className="px-3 py-2">
                      {row.type === "Time Adjustment" ? (
                        <input
                          type="datetime-local"
                          value={
                            row.returnDateTime
                              ? row.returnDateTime
                                  .replace("Z", "")
                                  .replace(".000", "")
                                  .substring(0, 16)
                              : ""
                          }
                          onChange={(e) =>
                            updateRow(
                              index,
                              "returnDateTime",
                              e.target.value || ""
                            )
                          }
                          className="w-full rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-900 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 focus:outline-none transition-colors"
                        />
                      ) : (
                        <span className="text-xs text-gray-300 pl-2">
                          —
                        </span>
                      )}
                    </td>

                    {/* Transaction Method — only for Transaction */}
                    <td className="px-3 py-2">
                      {row.type === "Transaction" ? (
                        <Select
                          options={transactionMethodOptions}
                          value={
                            transactionMethodOptions.find(
                              (o) =>
                                o.value === row.transactionMethodId
                            ) || null
                          }
                          onChange={(opt) =>
                            updateRow(
                              index,
                              "transactionMethodId",
                              opt?.value || null
                            )
                          }
                          placeholder="Select method..."
                          isClearable
                          styles={selectStyles}
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                        />
                      ) : (
                        <span className="text-xs text-gray-300 pl-2">
                          —
                        </span>
                      )}
                    </td>

                    {/* Description */}
                    <td className="px-3 py-2">
                      <input
                        type="text"
                        value={row.description || ""}
                        onChange={(e) =>
                          updateRow(index, "description", e.target.value)
                        }
                        placeholder="Optional"
                        className="w-full rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-900 placeholder-gray-400 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 focus:outline-none transition-colors"
                      />
                    </td>

                    {/* Remove button */}
                    <td className="px-3 py-2 text-center">
                      <button
                        onClick={() => removeRow(index)}
                        disabled={rows.length <= 1}
                        className="p-1 rounded-md text-gray-400 hover:text-red-500 hover:bg-red-50 disabled:opacity-25 disabled:hover:text-gray-400 disabled:hover:bg-transparent transition-colors"
                        title="Remove row"
                      >
                        <svg
                          className="h-4 w-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth="1.5"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
                          />
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Row count */}
      <div className="text-xs text-gray-400 text-right">
        {rows.length} row{rows.length !== 1 ? "s" : ""}
        {reservations.length > 0 &&
          ` · ${reservations.length} picked-up reservation${
            reservations.length !== 1 ? "s" : ""
          } available`}
      </div>
    </div>
  );
};

export default SmartBillPage;
